import { useRef } from 'react';
import { Camera, Check, X, AlertCircle, Image as ImageIcon, Upload, Shield } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export type ProofPackCategory = 'singles' | 'slabs' | 'sealed';

interface ProofPhotoType {
  key: string;
  label: string;
  description: string;
  required?: boolean;
}

// Possession is the same across all categories — handle + date written on paper, shot with the item.
const POSSESSION: ProofPhotoType = {
  key: 'possession',
  label: 'Proof of Possession',
  description: 'Handle + today\'s date on paper, with the item',
  required: true,
};

const SINGLES_TYPES: ProofPhotoType[] = [
  POSSESSION,
  { key: 'front', label: 'Front', description: 'Clear shot of the card front' },
  { key: 'back', label: 'Back', description: 'Clear shot of the card back' },
  { key: 'corners', label: 'All 4-8 Corners', description: 'Add 4 to 8 corner close-ups' },
  { key: 'holo_tilt', label: 'Holo Tilt', description: 'Show the holo effect at angle' },
];

const SLABS_TYPES: ProofPhotoType[] = [POSSESSION];

const SEALED_TYPES: ProofPhotoType[] = [POSSESSION];

export function getProofPhotoTypes(category: ProofPackCategory): ProofPhotoType[] {
  if (category === 'slabs') return SLABS_TYPES;
  if (category === 'sealed') return SEALED_TYPES;
  return SINGLES_TYPES;
}

// Flat union for legacy renderers (e.g. ProofPackGallery iterating saved jsonb)
export const ALL_PROOF_PHOTO_TYPES: ProofPhotoType[] = [
  POSSESSION,
  ...SINGLES_TYPES.slice(1),
  ...SLABS_TYPES.slice(1),
  ...SEALED_TYPES.slice(1),
];

// Backwards-compat export — singles set
export const PROOF_PHOTO_TYPES = SINGLES_TYPES;

export type ProofPhotoKey = string;

// Defect types for singles/slabs
export const CARD_DEFECT_TYPES = [
  { key: 'whitening', label: 'Whitening', description: 'Edge or corner whitening' },
  { key: 'dents', label: 'Dents', description: 'Surface indentations' },
  { key: 'scratches', label: 'Scratches', description: 'Surface scratches' },
  { key: 'bends', label: 'Bends/Creases', description: 'Bends or creases' },
  { key: 'print_lines', label: 'Print Lines', description: 'Factory print lines' },
  { key: 'holo_scratches', label: 'Holo Scratches', description: 'Scratches on holo layer' },
] as const;

export const SEALED_DEFECT_TYPES = [
  { key: 'shrink_tear', label: 'Shrink Tear', description: 'Tear in shrink wrap' },
  { key: 'box_damage', label: 'Box Damage', description: 'Dents or crushing' },
  { key: 'seal_broken', label: 'Seal Broken', description: 'Factory seal compromised' },
  { key: 'corner_wear', label: 'Corner Wear', description: 'Worn or damaged corners' },
  { key: 'water_damage', label: 'Water Damage', description: 'Water stains or warping' },
  { key: 'sun_fade', label: 'Sun Fade', description: 'Fading from sun exposure' },
] as const;

export const DEFECT_TYPES = [...CARD_DEFECT_TYPES, ...SEALED_DEFECT_TYPES] as const;
export type DefectType = typeof DEFECT_TYPES[number]['key'];

export interface ProofImages {
  possession?: string;
  front?: string;
  back?: string;
  corners?: string;
  holo_tilt?: string;
  slab_front?: string;
  slab_back?: string;
  sealed_front?: string;
  sealed_back?: string;
}

export interface DefectChecklist {
  [key: string]: boolean | undefined;
}

interface ProofPackUploadProps {
  proofImages: Record<string, File>;
  setProofImages: (images: Record<string, File>) => void;
  category?: ProofPackCategory;
  username?: string | null;
  quantity?: number;
  className?: string;
}

export function ProofPackUpload({
  proofImages,
  setProofImages,
  category = 'singles',
  username,
  quantity = 1,
  className,
}: ProofPackUploadProps) {
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const cameraInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const photoTypes = getProofPhotoTypes(category);

  const MAX_CORNERS = 8;
  const MIN_CORNERS = 4;
  const cornerKeys = Object.keys(proofImages)
    .filter((k) => k === 'corners' || k.startsWith('corners_'))
    .sort();

  const handlePhotoUpload = async (photoType: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target;
    const { processSelectedFiles, resetFileInput } = await import('@/lib/photoInputUtils');
    const files = await processSelectedFiles(target.files, {
      multiple: photoType === 'corners',
    });
    resetFileInput(target);
    if (files.length === 0) return;
    if (photoType === 'corners') {
      const next = { ...proofImages };
      let idx = 1;
      const used = new Set(cornerKeys);
      for (const file of files) {
        if (used.size >= MAX_CORNERS) break;
        while (used.has(`corners_${idx}`) || (idx === 1 && used.has('corners'))) idx++;
        const key = `corners_${idx}`;
        next[key] = file;
        used.add(key);
        idx++;
      }
      setProofImages(next);
    } else {
      setProofImages({ ...proofImages, [photoType]: files[0] });
    }
  };

  const removePhoto = (photoType: string) => {
    const newImages = { ...proofImages };
    if (photoType === 'corners') {
      cornerKeys.forEach((k) => delete newImages[k]);
    } else {
      delete newImages[photoType];
    }
    setProofImages(newImages);
  };

  const removeCornerAt = (key: string) => {
    const newImages = { ...proofImages };
    delete newImages[key];
    setProofImages(newImages);
  };

  const completedPhotos = photoTypes.filter((pt) =>
    pt.key === 'corners' ? cornerKeys.length >= MIN_CORNERS : !!proofImages[pt.key]
  ).length;
  const hasPossession = !!proofImages.possession;
  const isComplete = hasPossession && (
    category === 'singles'
      ? completedPhotos >= 4 // possession + 3 of front/back/corners/holo
      : true // slabs/sealed: only possession is required; rest are optional
  );

  const handleHint = username ? `@${username}` : '@yourhandle';

  return (
    <div className={cn('space-y-6', className)}>
      {isComplete && (
        <div className="flex justify-end">
          <div className="flex items-center gap-1 text-xs text-success bg-success/10 px-2 py-1 rounded-full">
            <Check className="w-3 h-3" />
            Complete
          </div>
        </div>
      )}

      {quantity > 1 && (() => {
        const unit = category === 'sealed' ? 'boxes' : category === 'slabs' ? 'slabs' : 'cards';
        return (
          <div className="-mb-2 rounded-lg bg-white/[0.03] px-3 py-2.5">
            <p className="text-xs text-foreground leading-relaxed">
              <span className="font-semibold">Stack of {quantity} — show them all in one photo.</span>{' '}
              Lay out all {quantity} {unit} together with your handle + today's date on paper. One photo covers the whole stack.
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed mt-1.5">
              Different conditions? Split into separate items instead — one Proof of Possession per condition.
            </p>
          </div>
        );
      })()}

      {/* Proof of Possession — minimal CTA / compact uploaded row */}
      {(() => {
        const possessionFile = proofImages.possession;
        const possessionPreview = possessionFile ? URL.createObjectURL(possessionFile) : null;
        return (
          <div className="relative">
            <input
              ref={(el) => { fileInputRefs.current.possession = el; }}
              type="file"
              accept="image/*,.heic,.heif,.dng"
              className="hidden"
              onChange={(e) => handlePhotoUpload('possession', e)}
            />
            <input
              ref={(el) => { cameraInputRefs.current.possession = el; }}
              type="file"
              accept="image/*,.heic,.heif,.dng"
              capture="environment"
              className="hidden"
              onChange={(e) => handlePhotoUpload('possession', e)}
            />
            <DropdownMenu>
              {possessionFile && possessionPreview ? (
                <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/20 p-2">
                  <button
                    type="button"
                    onClick={() => fileInputRefs.current.possession?.click()}
                    className="relative w-14 h-14 rounded-md overflow-hidden bg-black/40 shrink-0"
                    aria-label="Replace Proof of Possession"
                  >
                    <img src={possessionPreview} alt="Proof of Possession" className="w-full h-full object-cover" />
                  </button>
                  <div className="flex-1 flex items-center gap-1.5 text-xs text-success font-medium">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                    Added
                  </div>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="h-9 px-3 rounded-md text-xs font-semibold bg-white/[0.05] hover:bg-white/[0.08] text-foreground border border-white/10 transition-colors flex items-center gap-1.5"
                    >
                      <Camera className="w-3.5 h-3.5" />
                      Replace
                    </button>
                  </DropdownMenuTrigger>
                  <button
                    type="button"
                    onClick={() => removePhoto('possession')}
                    className="h-9 w-9 flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-white/[0.05] transition-colors"
                    aria-label="Remove Proof of Possession"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="w-full h-12 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold bg-white/[0.05] hover:bg-white/[0.08] text-foreground border border-white/10 transition-colors"
                  >
                    <Camera className="w-4 h-4" />
                    Add Proof of Possession Photo
                  </button>
                </DropdownMenuTrigger>
              )}
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={() => cameraInputRefs.current.possession?.click()}>
                  <Camera className="w-4 h-4 mr-2" />
                  Take Photo
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => fileInputRefs.current.possession?.click()}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Photo
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      })()}

      {/* Photo Upload Grid (non-possession photos) */}
      {photoTypes.some((pt) => pt.key !== 'possession') && (
      <div className="grid grid-cols-2 gap-3">
        {photoTypes.filter((pt) => pt.key !== 'possession').map((photoType) => {
          const isCorners = photoType.key === 'corners';
          const isPossession = photoType.key === 'possession';
          const hasPhoto = isCorners ? cornerKeys.length > 0 : !!proofImages[photoType.key];
          const preview = !isCorners && hasPhoto ? URL.createObjectURL(proofImages[photoType.key]) : null;
          const cornersFull = isCorners && cornerKeys.length >= MAX_CORNERS;
          const cornersComplete = isCorners && cornerKeys.length >= MIN_CORNERS;
          const allowMultiple = isCorners;

          return (
            <div key={photoType.key} className={cn('relative', photoTypes.length === 1 && 'w-1/2 max-w-[180px]')}>
              <input
                ref={(el) => { fileInputRefs.current[photoType.key] = el; }}
                type="file"
                accept="image/*,.heic,.heif,.dng"
                multiple={allowMultiple}
                className="hidden"
                onChange={(e) => handlePhotoUpload(photoType.key, e)}
              />
              <input
                ref={(el) => { cameraInputRefs.current[photoType.key] = el; }}
                type="file"
                accept="image/*,.heic,.heif,.dng"
                capture="environment"
                multiple={allowMultiple}
                className="hidden"
                onChange={(e) => handlePhotoUpload(photoType.key, e)}
              />

              {isCorners && hasPhoto ? (
                <div
                  className={cn(
                    'w-full aspect-square rounded-lg border overflow-hidden p-1.5',
                    cornersComplete ? 'border-success/50 bg-success/10' : 'border-white/[0.08] bg-black/20'
                  )}
                >
                  <div className="grid grid-cols-3 gap-1 h-full">
                    {cornerKeys.map((k) => {
                      const url = URL.createObjectURL(proofImages[k]);
                      return (
                        <div key={k} className="relative rounded overflow-hidden bg-black/40">
                          <img src={url} alt="corner" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeCornerAt(k);
                            }}
                            className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-black/70 text-white hover:bg-destructive transition-colors"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      );
                    })}
                    {!cornersFull && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="rounded border border-dashed border-white/20 bg-black/20 hover:bg-black/30 hover:border-white/40 flex flex-col items-center justify-center text-muted-foreground transition-all"
                          >
                            <span className="text-base leading-none">+</span>
                            <span className="text-[8px] mt-0.5">{cornerKeys.length}/{MAX_CORNERS}</span>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="center" className="w-44">
                          <DropdownMenuItem onClick={() => cameraInputRefs.current[photoType.key]?.click()}>
                            <Camera className="w-4 h-4 mr-2" />
                            Take Photo
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => fileInputRefs.current[photoType.key]?.click()}>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Photos
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              ) : hasPhoto && preview ? (
                <button
                  type="button"
                  onClick={() => fileInputRefs.current[photoType.key]?.click()}
                  className="w-full aspect-square rounded-lg border border-success/50 bg-success/10 transition-all flex flex-col items-center justify-center gap-1 overflow-hidden"
                >
                  <img
                    src={preview}
                    alt={photoType.label}
                    className="w-full h-full object-cover"
                  />
                </button>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="w-full aspect-square rounded-lg border border-white/[0.08] bg-black/20 hover:border-white/20 hover:bg-black/30 transition-all flex flex-col items-center justify-center gap-1 overflow-hidden p-2"
                    >
                      {isPossession ? (
                        <div className="relative w-7 h-7 flex items-center justify-center">
                          <Shield className="w-7 h-7 text-active-gold fill-active-gold/15" strokeWidth={2} />
                          <Check
                            className="absolute w-4 h-4 text-success drop-shadow-[0_0_4px_rgba(52,211,153,0.7)]"
                            strokeWidth={3.5}
                            style={{ top: '52%', left: '50%', transform: 'translate(-50%, -50%)' }}
                          />
                        </div>
                      ) : (
                        <ImageIcon className="w-6 h-6 text-muted-foreground" />
                      )}
                      <span className="text-xs font-medium text-center leading-tight">{photoType.label}</span>
                      <span className="text-[10px] text-muted-foreground text-center px-1 leading-tight">
                        {photoType.description}
                      </span>
                      {photoType.required && (
                        <span className="text-[9px] font-semibold uppercase tracking-wide text-active-gold">
                          Required
                        </span>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="w-44">
                    <DropdownMenuItem onClick={() => cameraInputRefs.current[photoType.key]?.click()}>
                      <Camera className="w-4 h-4 mr-2" />
                      Take Photo
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => fileInputRefs.current[photoType.key]?.click()}>
                      <Upload className="w-4 h-4 mr-2" />
                      {isCorners ? 'Upload Photos' : 'Upload Photo'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Remove all (non-corners only) */}
              {hasPhoto && !isCorners && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removePhoto(photoType.key);
                  }}
                  className="absolute top-1 right-1 p-1 rounded-full bg-black/70 text-white hover:bg-destructive transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              )}

              {/* Check indicator */}
              {hasPhoto && (!isCorners || cornersComplete) && (
                <div className="absolute bottom-1 right-1 p-1 rounded-full bg-success text-white">
                  <Check className="w-3 h-3" />
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {/* Progress indicator — bar on top, count centered below */}
      {(() => {
        const target = category === 'singles' ? 4 : 1;
        const done = category === 'singles' ? completedPhotos : (hasPossession ? 1 : 0);
        const pct = Math.min(100, (done / target) * 100);
        return (
          <div className="space-y-1.5 text-xs text-muted-foreground">
            <div className="h-1.5 bg-border/50 rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full transition-all duration-300',
                  isComplete ? 'bg-success' : 'bg-primary'
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="text-center">{done}/{target} required</div>
          </div>
        );
      })()}

    </div>
  );
}
